import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Select, { type StylesConfig } from "react-select";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import { useGeofencesStore } from "../../../store/admin/geofencesStore";
import ReportMinMaxInput from "../../reports/components/ReportMinMaxInput";
import ReportTemperatureScaleToggle from "../../reports/components/ReportTemperatureScaleToggle";
import { Segmented } from "../QuickReportViews";
import type { UnitRef } from "../quickReports.compute";
import {
  addRecipients,
  allowedFrequencies,
  MAX_RECIPIENTS,
  maxUnits,
  PERIOD_TEXT,
  reportMeta,
  SCHEDULABLE,
  SCHEDULABLE_FULL,
  switchReport,
  validateSchedule,
  WEEKDAYS,
  type FileFormat,
  type Frequency,
  type ParamsDraft,
  type ReportKind,
  type ScheduledReportId,
  type ScheduleForm,
} from "./scheduled";

type Props = {
  initial: ScheduleForm;
  editing: boolean;
  units: UnitRef[];
  unitsLoading: boolean;
  isDarkMode: boolean;
  saving: boolean;
  onSave: (input: ScheduleForm) => void;
  onClose: () => void;
};

type Option = { value: string; label: string };

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "daily", label: "Diario" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensual" },
];
const FORMATS: { value: FileFormat; label: string; icon: string }[] = [
  { value: "xlsx", label: "Excel", icon: "ri-file-excel-2-line" },
  { value: "pdf", label: "PDF", icon: "ri-file-pdf-2-line" },
];

// Panel lateral (pantalla completa en móvil) con el formulario entero. Esc o
// la X cierran sin guardar; el foco vuelve a donde estaba.
export default function ScheduleEditor({ initial, editing, units, unitsLoading, isDarkMode, saving, onSave, onClose }: Props) {
  const [form, setForm] = useState<ScheduleForm>(initial);
  const [switchNote, setSwitchNote] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [draftNote, setDraftNote] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const ref = useRef<HTMLFormElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLInputElement>("input")?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, []);

  const set = <K extends keyof ScheduleForm>(key: K, value: ScheduleForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setParam = <K extends keyof ParamsDraft>(key: K, value: ParamsDraft[K]) => setForm((f) => ({ ...f, params: { ...f.params, [key]: value } }));
  const pickReport = (kind: ReportKind, id: ScheduledReportId) => {
    const r = switchReport(form, kind, id);
    setForm(r.form);
    setSwitchNote(r.note);
  };

  // El catálogo de geocercas de la compañía, como lo carga la sección Reportes
  // › Geocercas; sólo cuando se elige ese reporte.
  const geofences = useGeofencesStore((s) => s.geofences);
  const geofencesLoading = useGeofencesStore((s) => s.loading);
  const fetchGeofences = useGeofencesStore((s) => s.fetchGeofences);
  const wantsGeofences = form.kind === "full" && form.report === "geofences";
  useEffect(() => {
    if (wantsGeofences) fetchGeofences({ limit: 1200 });
  }, [wantsGeofences, fetchGeofences]);
  const errors = useMemo(() => validateSchedule(form), [form]);
  const shown = tried ? errors : {};

  const commitDraft = (text = draft) => {
    if (!text.trim()) return;
    const r = addRecipients(form.recipients, text);
    set("recipients", r.recipients);
    setDraft(r.invalid.join(" "));
    setDraftNote(r.invalid.length ? `No parece un correo: ${r.invalid.join(", ")}` : r.overflow ? `Máximo ${MAX_RECIPIENTS} correos.` : null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.keys(errors).length || draft.trim()) {
      if (draft.trim()) commitDraft();
      return;
    }
    onSave(form);
  };

  const dark = isDarkMode;
  const muted = dark ? "text-gray-400" : "text-gray-500";
  const strong = dark ? "text-white" : "text-gray-900";
  const field = `w-full h-10 rounded-lg border px-3 text-sm outline-none transition-[border-color,box-shadow] duration-150 focus:ring-2 focus:ring-primary-500 ${
    dark ? "bg-dark-surface border-dark-border text-white placeholder:text-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
  }`;
  const chip = (on: boolean) =>
    `inline-flex items-center justify-center gap-1.5 rounded-lg border text-sm font-medium transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
      on
        ? "border-primary-500 bg-primary-500/15 text-primary-500"
        : `${dark ? "border-dark-border text-gray-300 hover:bg-white/5" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`
    }`;

  const isFull = form.kind === "full";
  const isBitacora = !isFull && form.report === "bitacora";
  const cap = isFull ? maxUnits(form.report) : null;
  const allowed = allowedFrequencies(form.kind, form.report);
  const reportName = reportMeta(form.report, form.kind).name;
  const options: Option[] = units.map((u) => ({ value: u.id, label: u.plate ? `${u.name} · ${u.plate}` : u.name }));
  const picked = options.filter((o) => form.device_ids.includes(o.value));
  const selectStyles = getCustomSelectStyles(dark) as StylesConfig<Option, boolean>;
  const geofenceById = new Map(geofences.map((g) => [g.id, { value: g.id, label: g.name }]));
  // Una geocerca guardada que ya no está en el catálogo se ve (y se puede
  // quitar) en vez de viajar escondida.
  const pickedGeofences = form.params.geofences.map(
    (id) => geofenceById.get(id) ?? { value: id, label: geofencesLoading ? "Cargando…" : "Geocerca que ya no existe" },
  );
  const numField = `${field} pr-14 tabular-nums`;
  const unit = (text: string) => <span className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs ${muted}`}>{text}</span>;
  // `one` va en el nombre accesible: Kilometraje y Paradas están en los dos grupos.
  const groups: { kind: ReportKind; label: string; one: string; items: { id: ScheduledReportId; name: string; icon: string }[] }[] = [
    { kind: "quick", label: "Informes rápidos", one: "informe rápido", items: SCHEDULABLE },
    { kind: "full", label: "Reportes completos", one: "reporte completo", items: SCHEDULABLE_FULL },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div aria-hidden className="absolute inset-0 bg-black/40 motion-safe:animate-fadeIn" />
      <form
        ref={ref}
        onSubmit={submit}
        noValidate
        role="dialog"
        aria-modal="true"
        aria-label={editing ? "Editar programación" : "Nueva programación"}
        className={`relative w-full sm:w-[480px] h-full flex flex-col shadow-2xl border-l motion-safe:animate-slideIn ${
          dark ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200"
        }`}
      >
        <header className={`flex items-center gap-3 px-5 py-4 border-b ${dark ? "border-dark-border" : "border-gray-200"}`}>
          <div className="min-w-0 flex-1">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${muted}`}>Informes programados</p>
            <h2 className={`text-lg font-semibold leading-tight ${strong}`}>{editing ? "Editar programación" : "Nueva programación"}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              dark ? "text-gray-300 hover:bg-white/10" : "text-gray-600 hover:bg-black/5"
            }`}
          >
            <i className="ri-close-line text-xl" aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 flex flex-col gap-6">
          <Field label="Nombre" htmlFor="sr-name" error={shown.name} isDarkMode={dark}>
            <input id="sr-name" className={field} maxLength={80} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ej. Kilometraje semanal de reparto" />
          </Field>

          <Field label="Informe" isDarkMode={dark}>
            <div role="radiogroup" aria-label="Informe" className="flex flex-col gap-4">
              {groups.map((g) => (
                <div key={g.kind}>
                  <p className={`mb-2 text-[11px] font-semibold uppercase tracking-wider ${muted}`}>{g.label}</p>
                  <div className={`grid gap-2 ${g.kind === "full" ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}>
                    {g.items.map((r) => {
                      const on = form.kind === g.kind && form.report === r.id;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          aria-label={`${r.name} (${g.one})`}
                          onClick={() => pickReport(g.kind, r.id)}
                          className={`${chip(on)} !justify-start px-3 py-2.5 text-left`}
                        >
                          <i className={`${r.icon} text-base flex-shrink-0`} aria-hidden />
                          <span className="min-w-0 leading-tight">{r.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {switchNote && (
              <p key={switchNote} role="status" className="mt-2 text-xs text-amber-600 motion-safe:animate-detailIn">
                {switchNote}
              </p>
            )}
          </Field>

          {isFull && (
            // key: al cambiar de reporte el bloque entra de nuevo y se nota que
            // los parámetros son otros.
            <div key={form.report} className="flex flex-col gap-6 motion-safe:animate-detailIn empty:hidden">
              {form.report === "speed" && (
                <Field label="Límite de velocidad" htmlFor="sr-minSpeed" error={shown.minSpeed} isDarkMode={dark} hint="Cuenta como exceso todo lo que vaya a esta velocidad o más.">
                  <div className="relative w-40">
                    <input id="sr-minSpeed" inputMode="decimal" className={numField} value={form.params.minSpeed} onChange={(e) => setParam("minSpeed", e.target.value)} placeholder="80" />
                    {unit("km/h")}
                  </div>
                </Field>
              )}
              {form.report === "driving" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Velocidad máxima" htmlFor="sr-maxSpeed" error={shown.maxSpeed} isDarkMode={dark}>
                    <div className="relative">
                      <input id="sr-maxSpeed" inputMode="decimal" className={numField} value={form.params.maxSpeed} onChange={(e) => setParam("maxSpeed", e.target.value)} placeholder="120" />
                      {unit("km/h")}
                    </div>
                  </Field>
                  <Field label="Revoluciones máx" htmlFor="sr-rpm" error={shown.rpm} isDarkMode={dark}>
                    <div className="relative">
                      <input id="sr-rpm" inputMode="numeric" className={numField} value={form.params.rpm} onChange={(e) => setParam("rpm", e.target.value)} placeholder="2500" />
                      {unit("rpm")}
                    </div>
                  </Field>
                </div>
              )}
              {form.report === "temperature" && (
                <Field label="Escala y límites (bajo–alto)" error={shown.limits} isDarkMode={dark} hint="Las lecturas fuera de este rango se marcan como fuera de límite.">
                  <div className="flex flex-wrap items-center gap-3">
                    <ReportTemperatureScaleToggle value={form.params.tempScale} onChange={(v) => setParam("tempScale", v)} />
                    <ReportMinMaxInput
                      lowValue={form.params.lowLimit}
                      highValue={form.params.highLimit}
                      onChangeLow={(v) => setParam("lowLimit", v)}
                      onChangeHigh={(v) => setParam("highLimit", v)}
                      lowPlaceholder="2"
                      highPlaceholder="11"
                      unit={form.params.tempScale === "fahrenheit" ? "°F" : "°C"}
                    />
                  </div>
                </Field>
              )}
              {form.report === "geofences" && (
                <Field label="Geocercas" htmlFor="sr-geofences" error={shown.geofences} isDarkMode={dark}>
                  <Select<Option, true>
                    inputId="sr-geofences"
                    isMulti
                    isLoading={geofencesLoading}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    options={[...geofenceById.values()]}
                    value={pickedGeofences}
                    onChange={(sel) => setParam("geofences", sel.map((o) => o.value))}
                    placeholder="Elige las geocercas"
                    noOptionsMessage={() => "Sin geocercas"}
                    loadingMessage={() => "Cargando geocercas…"}
                    menuPortalTarget={document.body}
                    styles={{ ...(selectStyles as unknown as StylesConfig<Option, true>), menuPortal: (b) => ({ ...b, zIndex: 70 }) }}
                  />
                </Field>
              )}
              {(form.report === "historic" || form.report === "events") && (
                <p className={`flex items-start gap-2 text-xs ${muted}`}>
                  <i className="ri-map-pin-line mt-px" aria-hidden />
                  Sale sin direcciones: en los programados la geocodificación va apagada.
                </p>
              )}
            </div>
          )}

          <Field
            label={isBitacora ? "Unidad" : "Unidades"}
            htmlFor="sr-units"
            error={shown.device_ids}
            isDarkMode={dark}
            aside={cap ? <span className={`tabular-nums ${form.device_ids.length >= cap ? "text-amber-600" : ""}`}>{form.device_ids.length} de {cap}</span> : undefined}
            hint={
              isBitacora
                ? "La bitácora es de una sola unidad."
                : cap
                  ? `Elige al menos una, hasta ${cap}.`
                  : "Vacío = todas, también las que des de alta después."
            }
          >
            <Select<Option, boolean>
              inputId="sr-units"
              isMulti={!isBitacora}
              isLoading={unitsLoading}
              closeMenuOnSelect={isBitacora}
              options={options}
              // Al llegar al tope ya no se puede agregar otra; las elegidas se siguen quitando.
              isOptionDisabled={() => !!cap && form.device_ids.length >= cap}
              value={isBitacora ? picked[0] ?? null : picked}
              onChange={(sel) => set("device_ids", sel ? ([] as Option[]).concat(sel as Option | Option[]).map((o) => o.value) : [])}
              placeholder={isBitacora ? "Elige la unidad" : isFull ? "Elige las unidades" : `Todas las unidades${units.length ? ` (${units.length})` : ""}`}
              noOptionsMessage={() => "Sin unidades"}
              loadingMessage={() => "Cargando unidades…"}
              menuPortalTarget={document.body}
              styles={{ ...selectStyles, menuPortal: (b) => ({ ...b, zIndex: 70 }) }}
            />
          </Field>

          <Field
            label="Frecuencia"
            isDarkMode={dark}
            hint={`Cada envío incluye ${PERIOD_TEXT[form.frequency]}.${
              allowed.includes("monthly") ? "" : ` ${reportName} va punto por punto: un mes entero no cabe en un correo, por eso sólo diario o semanal.`
            }`}
          >
            <div className="flex flex-col gap-3">
              <Segmented
                label="Frecuencia"
                value={form.frequency}
                options={FREQUENCIES.map((o) => (allowed.includes(o.value) ? o : { ...o, disabled: true, title: `${reportName} sólo se programa diario o semanal` }))}
                onChange={(v) => set("frequency", v)}
                isDarkMode={dark}
              />
              {form.frequency === "weekly" && (
                <div role="radiogroup" aria-label="Día de la semana" className="grid grid-cols-7 gap-1.5">
                  {WEEKDAYS.map((name, i) => (
                    <button
                      key={name}
                      type="button"
                      role="radio"
                      aria-checked={form.weekday === i + 1}
                      aria-label={name}
                      title={name}
                      onClick={() => set("weekday", i + 1)}
                      className={`${chip(form.weekday === i + 1)} h-10 capitalize`}
                    >
                      {name.slice(0, 2)}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {form.frequency === "monthly" && (
                  <>
                    <label htmlFor="sr-dom" className={`text-sm ${muted}`}>
                      El día
                    </label>
                    <select id="sr-dom" className={`${field} !w-20`} value={form.day_of_month ?? 1} onChange={(e) => set("day_of_month", Number(e.target.value))}>
                      {Array.from({ length: 28 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label htmlFor="sr-time" className={`text-sm ${muted}`}>
                  a las
                </label>
                <input id="sr-time" type="time" step={900} className={`${field} !w-32 tabular-nums`} value={form.time} onChange={(e) => set("time", e.target.value)} />
              </div>
              {(shown.frequency || shown.weekday || shown.day_of_month || shown.time) && (
                <p className="text-xs text-red-500">{shown.frequency || shown.weekday || shown.day_of_month || shown.time}</p>
              )}
            </div>
          </Field>

          <Field label="Formatos" error={shown.formats} isDarkMode={dark}>
            <div className="flex gap-2">
              {FORMATS.map((f) => {
                const on = form.formats.includes(f.value);
                return (
                  <button
                    key={f.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set("formats", on ? form.formats.filter((x) => x !== f.value) : [...form.formats, f.value])}
                    className={`${chip(on)} h-10 px-4`}
                  >
                    <i className={f.icon} aria-hidden /> {f.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field
            label="Destinatarios"
            htmlFor="sr-to"
            error={shown.recipients}
            isDarkMode={dark}
            aside={<span className="tabular-nums">{form.recipients.length} de {MAX_RECIPIENTS}</span>}
          >
            <div
              className={`min-h-10 w-full rounded-lg border px-2 py-1.5 flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-primary-500 ${
                dark ? "bg-dark-surface border-dark-border" : "bg-white border-gray-300"
              }`}
              onClick={() => document.getElementById("sr-to")?.focus()}
            >
              {form.recipients.map((email) => (
                <span key={email} className="inline-flex items-center gap-1 max-w-full rounded-md bg-primary-500/15 text-primary-500 pl-2 pr-1 py-0.5 text-xs font-medium">
                  <span className="truncate">{email}</span>
                  <button
                    type="button"
                    aria-label={`Quitar ${email}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      set("recipients", form.recipients.filter((x) => x !== email));
                    }}
                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-primary-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <i className="ri-close-line" aria-hidden />
                  </button>
                </span>
              ))}
              <input
                id="sr-to"
                type="email"
                inputMode="email"
                autoComplete="off"
                value={draft}
                disabled={form.recipients.length >= MAX_RECIPIENTS}
                onChange={(e) => {
                  setDraftNote(null);
                  // Pegar o escribir un separador confirma lo anterior.
                  if (/[\s,;]$/.test(e.target.value)) commitDraft(e.target.value);
                  else setDraft(e.target.value);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  commitDraft(`${draft} ${e.clipboardData.getData("text")}`);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitDraft();
                  } else if (e.key === "Backspace" && !draft && form.recipients.length) {
                    set("recipients", form.recipients.slice(0, -1));
                  }
                }}
                onBlur={() => commitDraft()}
                placeholder={form.recipients.length ? "" : "correo@empresa.com"}
                className={`flex-1 min-w-[10rem] h-7 bg-transparent text-sm outline-none ${dark ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"}`}
              />
            </div>
            {draftNote && <p className="mt-1.5 text-xs text-amber-600">{draftNote}</p>}
          </Field>
        </div>

        <footer className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${dark ? "border-dark-border" : "border-gray-200"}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 h-10 rounded-xl text-sm font-medium transition-colors duration-150 ${dark ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-gray-100"}`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-white disabled:opacity-60 transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            {saving && <i className="ri-loader-4-line animate-spin" aria-hidden />}
            {editing ? "Guardar cambios" : "Programar"}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  aside,
  isDarkMode,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  aside?: ReactNode;
  isDarkMode: boolean;
  children: ReactNode;
}) {
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const Label = htmlFor ? "label" : "p";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <Label {...(htmlFor ? { htmlFor } : {})} className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
          {label}
        </Label>
        {aside && <span className={`text-xs ${muted}`}>{aside}</span>}
      </div>
      {children}
      {error ? <p className="mt-1.5 text-xs text-red-500">{error}</p> : hint ? <p className={`mt-1.5 text-xs ${muted}`}>{hint}</p> : null}
    </div>
  );
}
